import alt from 'components/Dispatcher';
import AdminEditUserSource from 'sources/admin/user/AdminEditUserSource';
import {AvatarSource} from 'sources/AvatarSource';
import generateAsyncActions from 'utils/generateAsyncActions';

class AdminEditUserActions {
  constructor() {
    this.generateActions(
      ...generateAsyncActions(
        // Actions related to create/update/get a user
        'create',
        'update',
        'get',
        // Triggered when the admin changes the dashboard mode dropdown
        'changeDashboardMode',
        // Actions related to various fields on the user edit/create page. See
        // web/src/utils/createConnectedField.js for details
        'changeField',
        'blurField',
        // Actions related to the 'User - Edit' page's form submission
        'reset',
        'submitForm',
        'checkFormErrors',
        'validateAndSubmitForm'
      )
    );
  }

  /**
   * Triggered when an admin clicks "New User" to use the User form to create a user
   */
  reset() {
    return dispatch => dispatch();
  }

  /**
   * Triggered when an admin clicks "Save" on the 'User - Edit' page
   * @return {function(*): *}
   */
  checkFormErrors() {
    return dispatch => dispatch();
  }

  /**
   * Triggered when the admin changes the dashboard mode of the user
   * @param args {Any}
   * @returns {function(*): void}
   */
  changeDashboardMode(args) {
    return dispatch => {
      dispatch(args);
    };
  }

  /**
   * Triggered when the admin clicks "Save" on the 'Edit - User' page
   * @param userId {string}
   * @param fieldValues {JSON}
   */
  submitForm(userId, fieldValues) {
    return dispatch => {
      dispatch();
      return AdminEditUserSource.submit(userId, fieldValues).then(
        this.submitFormSuccess,
        this.submitFormError
      );
    };
  }

  /**
   * Triggered when the admin clicks "Save" on the 'New - User' page
   * @param fieldValues {Object}
   */
  create(fieldValues) {
    return dispatch => {
      dispatch();
      return AdminEditUserSource.create(fieldValues).then(this.createSuccess, this.createError);
    };
  }

  /**
   * Callback triggered when loading the info of `userId`
   * @param userId id of the user to get
   * @returns {function(*): *}
   */
  get(userId) {
    return dispatch => {
      dispatch();
      return AdminEditUserSource.retrieve(userId).then(this.getSuccess, this.getError);
    };
  }

  /**
   * Callback triggered when changing the value of a field
   * @param field form field changed by the user
   * @param value form value provided by the user
   * @returns {function(*): void}
   */
  changeField(field, value) {
    return dispatch => {
      dispatch({field: field, value: value});
    };
  }

  /**
   * Callback triggered when blurring a field
   * @param field field blurred by the user
   * @returns {function(*): void}
   */
  blurField(field) {
    return dispatch => {
      dispatch({field: field});
    };
  }

  /**
   * Triggered when the admin clicks 'Save' on the 'Edit - User' or 'Profile' page
   * @param userId {string}
   * @param avatar {File}
   * @param fieldValues {EditUser}
   * @param fieldErrors {Map}
   * @param isEditing {boolean}
   */
  validateAndSubmitForm(userId, avatar, fieldValues, fieldErrors, isEditing) {
    let dispatcher;
    // TODO fieldErrors is empty if no form control has been modified yet.
    // i.e. When creating a new user, the form can be submitted with nothing filled in.
    const hasError = !fieldErrors.isEmpty();

    if (hasError) {
      dispatcher = this.checkFormErrors();
    } else if (isEditing) {
      dispatcher = this.submitForm(userId, fieldValues);
    } else {
      dispatcher = this.create(fieldValues);
    }

    if (!hasError && avatar) {
      AvatarSource.uploadAvatar(userId, avatar);
    }

    return dispatcher;
  }
}

export default alt.createActions(AdminEditUserActions);
